<script setup lang="ts">
import { computed, ref, nextTick, type PropType } from 'vue';
import { FormFieldType, type FormField } from '@/models/form';
import {
  ButtonType,
  CustomButton,
  EntryItemTextAndNumber,
  EntryItemChoice,
  EntryItemDropdown,
  EntryItemDate,
  EntryItemLocation,
  EntryItemSlider,
  EntryItemBarcode,
  EntryItemSignature,
  EntryItemDrilldown,
  EntryItemFile,
  EntryItemDescription,
  EntryItemMatrix,
  EntryItemUnit,
  resolveReferences,
  getFieldValue,
  getMatrixValues
} from '@/components';
import { useEntryStore } from '@/stores/entry';
import { useI18n } from 'vue-i18n';
import type { CaseModel, MatrixTableRow } from '@/models';
import EntryItemNumberRange from './EntryItemNumberRange.vue';
import SpData from '@/assets/SpData.svg';

const { t: $t } = useI18n();

const entryStore = useEntryStore();

const props = defineProps({
  field: {
    type: Object as PropType<FormField>,
    required: true
  },
  previewMode: {
    type: Boolean
  },
  selectedCase: {
    type: Object as PropType<CaseModel>
  }
});

const localField = ref<FormField>(props.field);
const sectionRef = ref<HTMLElement | null>(null);

const previewMode = computed(() => props.previewMode || props.field.disable);

const handleSectionToggle = () => {
  const wasCollapsed = !localField.value.sectionExpanded;
  entryStore.expandSectionFields(localField.value);
  if (wasCollapsed) {
    nextTick(() => {
      const el = document.querySelector(
        `[data-section-id="${localField.value.id}"]`
      );
      if (el) {
        el.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    });
  }
};

const lastSectionChildField = computed(() => {
  // Find the parent section field for this localField
  const parentSectionField = entryStore.form?.items?.find(
    item => item.identifier === localField.value.sectionId
  );
  if (parentSectionField && parentSectionField.options.fields) {
    const sectionItems = parentSectionField.options.fields
      .map(fieldId =>
        entryStore.form?.items?.find(item => item.identifier === fieldId)
      )
      .filter(Boolean) as FormField[];
    sectionItems.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    // Get all child fields in this section
    const childFields = [];
    for (const childField of sectionItems) {
      if (childField && childField.expanded && childField.show !== false) {
        // Only include fields that are visible
        childFields.push(childField);
      }
    }
    // Find the last visible child field
    const lastVisibleChild = childFields[childFields.length - 1];
    return lastVisibleChild?.identifier === localField.value.identifier;
  }
  return false;
});

const emit = defineEmits(['toNextSection', 'onSaveEntry']);

const scrollToNextSectionField = () => {
  emit('toNextSection', localField.value);
};

const inputSectionFieldsLength = computed(() => {
  const fieldIds = localField.value.options.fields;
  if (!fieldIds || !entryStore.form?.items) {
    return 0;
  }

  return fieldIds.filter(fieldId => {
    const field = entryStore.form?.items?.find(
      item => item.identifier === fieldId
    );
    return field && field.type !== FormFieldType.TITLE && field.show !== false;
  }).length;
});

const sectionCompletionInfo = computed(() => {
  const fieldIds = localField.value.options.fields;

  let completed = 0;
  let total = 0;

  fieldIds?.forEach(fieldId => {
    const field = entryStore.form?.items?.find(
      item => item.identifier === fieldId
    );
    if (field && field.type !== FormFieldType.TITLE && field.strictRequired && field.show !== false) {
      total += 1;
      const fieldValue = getFieldValue(field);
      if (
        field.type === FormFieldType.MATRIX &&
        fieldValue
      ) {
        if (getMatrixValues(fieldValue as MatrixTableRow[])) {
          completed += 1;
        }
      } else if (fieldValue) {
        completed += 1;
      }
    }
  });

  return total ? `(${completed} / ${total} completed)` : '';
});

const handleSaveEntry = () => {
  emit('onSaveEntry');
};
</script>

<template>
  <div class="root">
    <div class="spacer-top"
      v-if="
        localField.sectionId &&
        localField.showRequiredError &&
        localField.inputChanged
      "></div>
    <div class="item-base"
      :class="{
        'base-required':
          localField.showRequiredError && localField.inputChanged,
        'srcolled-to': localField.blink,
        'section-field': localField.sectionId,
        'section-field-last': lastSectionChildField,
      }"
      :data-section-id="localField.id">
      <div class="base-section"
        v-if="localField.type !== FormFieldType.BRANDING">
        <h3 class="field-label"
          v-if="
            localField.type !== FormFieldType.TITLE &&
            localField.type !== FormFieldType.SECTION
          ">
          {{ resolveReferences(entryStore.form?.items!, localField.label) }}
          <span class="required-mark"
            v-if="localField.strictRequired || localField.nonStrictRequired">*</span>
          <span class="recommended-mark"
            v-if="localField.recommended"
            v-tooltip="'Recommended'">*</span>
          <span class="optional-mark"
            v-if="localField.optional"
            v-tooltip="'Optional'">○</span>
          <FontAwesomeIcon :icon="[`fas`, `square-info`]"
            v-if="localField.options.tooltip"
            v-tooltip="localField.options.tooltip"
            class="tooltip-icon"/>
        </h3>
        <div class="title-field" v-if="localField.type === FormFieldType.TITLE">
          <div class="title-section">
            <h1 class="title-field">{{ localField.label }}</h1>
            <EntryItemDescription :description="localField.description"
              v-if="localField.description"/>
          </div>
        </div>
        <div class="title-and-section"
          v-if="localField.type === FormFieldType.SECTION"
          ref="sectionRef">
          <div class="section-title-container">
            <div>
              <h1 class="title-field">{{ localField.label }}</h1>
              <h4 class="field-count"
                v-if="localField.type === FormFieldType.SECTION">
                {{ inputSectionFieldsLength }} fields
                {{ sectionCompletionInfo }}
              </h4>
            </div>

            <CustomButton :text="localField.sectionExpanded ? 'Collapse' : 'Expand'"
              :type="ButtonType.OUTLINED"
              class="expand-button"
              @onClick="handleSectionToggle"
              v-if="localField.type === FormFieldType.SECTION"
              :icon="
                localField.sectionExpanded
                  ? { icon: 'caret-down', style: 'fas' }
                  : { icon: 'caret-right', style: 'fas' }
              "/>
          </div>
          <EntryItemDescription :description="localField.description"
            v-if="localField.description && localField.sectionExpanded"/>
        </div>

        <EntryItemDescription :description="localField.description"
          v-if="
            localField.description &&
            localField.type !== FormFieldType.TITLE &&
            localField.type !== FormFieldType.SECTION
          "/>
      </div>

      <div class="entry-section"
        v-if="
          localField.type !== FormFieldType.TITLE &&
          localField.type !== FormFieldType.SECTION &&
          localField.type !== FormFieldType.BRANDING
        ">
        <EntryItemTextAndNumber :field="localField"
          v-if="
            (localField.type === FormFieldType.TEXT ||
              localField.type === FormFieldType.NUMBER) &&
            !localField.options.range
          "
          :previewMode="previewMode"/>

        <EntryItemNumberRange v-model="localField.value!"
          :range="localField.options.range"
          :field="localField"
          v-if="
            localField.type === FormFieldType.NUMBER && localField.options.range
          "
          :previewMode="previewMode"/>

        <EntryItemChoice :field="localField"
          v-if="localField.type === FormFieldType.CHOICE"
          :previewMode="previewMode"/>

        <EntryItemDropdown :field="localField"
          v-if="localField.type === FormFieldType.DROPDOWN"
          :previewMode="previewMode"/>

        <EntryItemDate :field="localField"
          v-if="localField.type === FormFieldType.DATE"
          :selectedCase="selectedCase"
          :previewMode="previewMode"/>

        <EntryItemLocation :field="localField"
          v-if="localField.type === FormFieldType.LOCATION"
          :previewMode="previewMode"/>

        <EntryItemSlider :field="localField"
          v-if="localField.type === FormFieldType.SLIDER"
          :previewMode="previewMode"/>

        <EntryItemBarcode :field="localField"
          v-if="localField.type === FormFieldType.BARCODE"/>

        <EntryItemSignature :field="localField"
          v-if="localField.type === FormFieldType.SIGNATURE"
          :previewMode="previewMode"/>

        <EntryItemMatrix :field="localField"
          v-if="localField.type === FormFieldType.MATRIX"
          :selectedCase="selectedCase"
          :previewMode="previewMode"/>

        <EntryItemDrilldown :field="localField"
          v-if="localField.type === FormFieldType.DRILLDOWN"
          :previewMode="previewMode"/>

        <EntryItemFile :field="localField"
          v-if="localField.type === FormFieldType.FILE"
          :previewMode="previewMode"/>

        <EntryItemUnit :field="localField"
          v-if="localField.type === FormFieldType.UNIT"
          :previewMode="previewMode"/>
      </div>

      <div class="message-section"
        v-if="
          localField.warn ||
          localField.error ||
          localField.valid ||
          localField.showRequiredError ||
          localField.inputChanged ||
          localField.invalidInput
        ">
        <div class="alert warn" v-if="localField.warn || localField.valid">
          <FontAwesomeIcon :icon="['far', 'triangle-exclamation']"
            class="icon"/>
          <div class="label">{{ localField.warn || localField.valid }}</div>
        </div>
        <div class="alert error" v-if="localField.error">
          <FontAwesomeIcon :icon="['far', 'circle-exclamation']" class="icon" />
          <div class="label">{{ localField.error }}</div>
        </div>
        <div class="alert required"
          v-if="localField.showRequiredError && localField.inputChanged">
          <FontAwesomeIcon :icon="['far', 'triangle-exclamation']"
            class="icon"/>
          <div class="label">{{ $t("required_field_msg") }}</div>
        </div>
        <div class="alert required" v-if="localField.invalidInput">
          <FontAwesomeIcon :icon="['far', 'triangle-exclamation']"
            class="icon"/>
          <div class="label">{{ $t("invalid_incomplete_input") }}</div>
        </div>
      </div>

      <div class="next-section-button" v-if="lastSectionChildField">
        <CustomButton text="Close section"
          @onClick="scrollToNextSectionField"
          :type="ButtonType.OUTLINED"/>
      </div>

      <div class="save-button-container" v-if="localField.type === FormFieldType.SUBMIT_BUTTON">
        <CustomButton :text="entryStore.form?.entrySaveButtonText ?? $t('save')"
          @onClick="handleSaveEntry"
          :type="ButtonType.POSITIVE"/>
      </div>

      <div class="entry-anonymous-views"
        v-if="localField.type === FormFieldType.BRANDING">
        <div class="branding">
          <div class="powered-by">{{ $t("powered_by") }}</div>
          <a alt="Studypages Data"
            href="https://www.teamscopeapp.com"
            target="_blank"
            rel="noopener noreferrer">
            <img alt="Studypages Data" :src="SpData"/>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.item-base {
  padding: 36px 0px;
}
.base-required {
  margin-left: -20px;
  margin-right: -20px;
  padding-left: 20px;
  padding-right: 20px;
  background-color: #ffe5e8;
}
.spacer-top {
  height: 20px;
  margin-left: -20px;
  margin-right: -20px;
  border-left: 2px solid #a5c7fe;
  border-right: 2px solid #a5c7fe;
}
.srcolled-to {
  margin-left: -20px;
  margin-right: -20px;
  padding-left: 20px;
  padding-right: 20px;
  border-radius: 4px;
  animation: blink 1.5s ease-in-out;
}
@keyframes blink {
  0% {
    background-color: #ffffff;
  }
  25% {
    background-color: #e6edf8;
  }
  50% {
    background-color: #ffffff;
  }
  75% {
    background-color: #e6edf8;
  }
  100% {
    background-color: #ffffff; /* Final color */
  }
}
.field-label {
  color: #272a2e;
  font-size: 16px;
  font-weight: 700;
  line-height: 30px;

  .required-mark {
    color: #ea3546;
  }
  .recommended-mark {
    color: #6c757d;
  }
  .optional-mark {
    color: #0d6efd;
  }
}
.title-field {
  color: #272a2e;
  font-size: 20px;
  font-weight: 700;
  line-height: 30px;
  margin-right: 16px;
}
.title-and-section {
  padding: 16px 8px;
  margin-left: -20px;
  margin-right: -20px;
  margin-bottom: -36px;
  background-color: #d0e1fb;
  border: 2px solid #a5c7fe;
}
.section-title-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.section-field {
  margin-left: -20px;
  margin-right: -20px;
  padding-left: 20px;
  padding-right: 20px;
  border-left: 2px solid #a5c7fe;
  border-right: 2px solid #a5c7fe;
}
.section-field-last {
  border-bottom: 2px solid #a5c7fe;
}
.expand-button {
  background-color: #dde9fc;
}
.entry-section {
  padding-top: 16px;
}
.message-section {
  margin-top: 16px;
}
.alert {
  padding: 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  font-size: 16px;

  .icon {
    font-size: 20px;
    margin-right: 8px;
  }
}
.warn {
  color: #5c0a11;
  border: 1px solid #fa8e00;
  background-color: #fff9f2;

  .icon {
    color: #fa8e00;
  }
}
.required,
.error {
  color: #5c0a11;
  border: 1px solid #ea3546;
  background-color: #fef5f6;
  margin-top: 8px;

  .icon {
    color: #ea3546;
  }
}

.next-section-button {
  display: flex;
  justify-content: center;
  margin-top: 40px;
}

.save-button-container {
  display: flex;
  justify-content: center;
}

@media (max-width: 600px) {
  .item {
    &-base {
      padding: 15px 0 0;
    }
  }
  .title-and-section {
    margin: 0;
    padding: 8px;
  }
  .section-field {
    margin-left: 0;
    margin-right: 0;
    padding: 8px;
  }
  .title-field {
    margin-right: 0;
  }
  .spacer-top {
    margin-left: unset;
    margin-right: unset;
  }
  .entry-anonymous-views {
    .branding {
      margin: 0;
      padding: 40px 0 30px;
    }
  }
}

.entry-anonymous-views {
  .warning {
    color: #575e66;
    font-size: 12px;

    & > a {
      color: #575e66;
    }
  }

  .branding {
    text-align: center;

    .powered-by {
      color: #272a2e;
      font-size: 12px;
      line-height: 20px;
    }

    img {
      margin-top: 4px;
    }

    .own-forms {
      color: #272a2e;
      font-size: 12px;
      padding-top: 12px;
    }
  }
}
</style>
